import { Policy } from '@commandDrivenStateEngine/core/policies';
import { AccountState } from '../state';
import { AccountCommand } from '../commands';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
/**
 * SKU Config Submit Policy
 *
 * Responsibilities:
 * - React to account/skuConfig/save/start — call SetCategorySkuFormulaOverride, dispatch save/success or save/error
 * - React to account/skuConfig/remove/start — call RemoveCategorySkuFormulaOverride, dispatch remove/success or remove/error
 */
export const createSkuConfigSubmitPolicy = (
  backend: ClientLibraryType,
  accountId: string
): Policy<AccountState, AccountCommand> => {
  let configCreated = false;

  const ensureConfigExists = async () => {
    if ( configCreated ) return;
    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- idempotent config-create; a conflict when the config already exists is the expected path, and the real submit that follows surfaces its own errors (TV1-2010 waiver, not a rethrow).
    try {
      await backend.commands.CreateItemsPublishingAccountConfiguration({
        id: accountId,
        body: {}
      });
    } catch {
      // Idempotent — already exists, ignore
    }
    configCreated = true;
  };

  return ({
  name: 'sku-config-submit',

  shouldReact: ( prev, next ) => {
    // React when isSaving transitions from null to a categoryId
    return prev.skuConfig.isSaving === null && next.skuConfig.isSaving !== null;
  },

  onStateChange: async ({ prev, next, ctx }) => {
    const categoryId = next.skuConfig.isSaving;
    if ( !categoryId ) return;

    // Determine if this is a save or remove based on which command triggered the transition
    // If editingCategory is set, it's a save operation; if editingCategory is null, it's a remove operation
    const isSaveOperation = next.skuConfig.editingCategory !== null;

    if ( isSaveOperation ) {
      // Save override
      const formula = next.skuConfig.editingCategory!.formula;

      try {
        await ensureConfigExists();
        await backend.commands.SetCategorySkuFormulaOverride({
          id: accountId,
          body: { categoryId, formula }
        });

        ctx.dispatch(
          {
            type: 'account/skuConfig/save/success',
            payload: { categoryId, formula }
          },
          { source: 'api' }
        );
      } catch ( error ) {
        console.error( '[account] SKU config save error:', error );

        ctx.dispatch(
          { type: 'account/skuConfig/save/error', payload: {}},
          { source: 'api' }
        );

        throw error;
      }
    } else {
      // Remove override
      try {
        await ensureConfigExists();
        await backend.commands.RemoveCategorySkuFormulaOverride({
          id: accountId,
          body: { categoryId }
        });

        ctx.dispatch(
          {
            type: 'account/skuConfig/remove/success',
            payload: { categoryId }
          },
          { source: 'api' }
        );
      } catch ( error ) {
        console.error( '[account] SKU config remove error:', error );

        ctx.dispatch(
          { type: 'account/skuConfig/remove/error', payload: {}},
          { source: 'api' }
        );

        throw error;
      }
    }
  }
});
};
